import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Box } from '@rover/kibble/core';
import useOnIntersect from '@rover/react-lib/src/hooks/useOnIntersect';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import type { Callout, PetInfoQuestion, PetInfoQuestionCallout } from '@rover/types/src/Callout';
import type { SearchFilters } from '@rover/types/src/SearchFilters';

import { PetInfoCalloutDisplayed, PetInfoCalloutResponse } from '../components/analyticsEvents';
import PetInfoCallout from '../components/Callouts/PetInfoCallout';

import { DogSet } from './petInfoCalloutStorage';
import {
  CALLOUT_IDS,
  CALLOUT_RESPONSES,
  shouldRenderCallout,
  usePetInfoCalloutCore,
} from './usePetInfoCalloutCore';

export const getPetCalloutIndexMap = (
  callout: PetInfoQuestionCallout | undefined
): Record<number, PetInfoQuestion[]> => {
  if (!callout) return {};

  const validIds = Object.values(CALLOUT_IDS) as string[];

  const mapping = callout.data.questions
    .filter(({ id }) => validIds.includes(id))
    .reduce((acc, question) => {
      if (!acc[callout.position - 1]) {
        acc[callout.position - 1] = [];
      }
      acc[callout.position - 1].push(question);
      return acc;
    }, {} as Record<number, PetInfoQuestion[]>);

  return mapping;
};

interface TrackedPetInfoCalloutProps {
  callout: PetInfoQuestion;
  onChange: (question: PetInfoQuestion, value: string) => void;
  onClose: (question: PetInfoQuestion) => void;
}

const TrackedPetInfoCallout = ({
  callout,
  onChange,
  onClose,
}: TrackedPetInfoCalloutProps): JSX.Element => {
  const handleVisible = useCallback(() => {
    if (callout.id === CALLOUT_IDS.DOG_INTERACTION) {
      emitAnalyticsEvent(new PetInfoCalloutDisplayed());
    }
  }, [callout.id]);

  const ref = useOnIntersect(handleVisible, true, { threshold: 1.0 });

  return (
    <Box ref={ref} mb="1x">
      <PetInfoCallout callout={callout} onChange={onChange} onClose={onClose} />
    </Box>
  );
};

type UseCalloutsParams = {
  callouts: Callout[];
  dogSet: DogSet;
  updateFiltersAndFireSearchOnChange?: (filters: Partial<SearchFilters>) => void;
};
type UseCalloutReturnType = {
  renderCalloutAtIndex: (index: number) => JSX.Element | null;
  toastText: string | null;
  onToastClose: () => void;
};

export const usePetInfoCallout = ({
  callouts,
  dogSet,
  updateFiltersAndFireSearchOnChange,
}: UseCalloutsParams): UseCalloutReturnType => {
  const [isMounted, setIsMounted] = useState(false);
  const [toastText, setToastText] = useState<string | null>(null);
  const onToastClose = useCallback(() => setToastText(null), []);

  useEffect(() => setIsMounted(true), []);

  const {
    petInfoCallout,
    calloutAnswer,
    stableDogSet,
    handleChange: coreHandleChange,
    handleClose: coreHandleClose,
  } = usePetInfoCalloutCore({
    callouts,
    dogSet,
    updateFiltersOnChange: updateFiltersAndFireSearchOnChange,
    onShowToast: setToastText,
  });

  const calloutsPerIndex = useMemo(() => getPetCalloutIndexMap(petInfoCallout), [petInfoCallout]);

  const handleChange = useCallback(
    (question: PetInfoQuestion, value: string) => {
      coreHandleChange(question, value);
      if (question.id === CALLOUT_IDS.DOG_INTERACTION) {
        emitAnalyticsEvent(new PetInfoCalloutResponse({ response: value }));
      }
    },
    [coreHandleChange]
  );

  const handleClose = useCallback(
    (question: PetInfoQuestion) => {
      coreHandleClose(question);
      if (question.id === CALLOUT_IDS.DOG_INTERACTION) {
        emitAnalyticsEvent(new PetInfoCalloutResponse({ response: CALLOUT_RESPONSES.DISMISSED }));
      }
    },
    [coreHandleClose]
  );

  const renderCalloutAtIndex = useCallback(
    (index: number): JSX.Element | null => {
      if (!isMounted) return null;
      const calloutsForIndex = calloutsPerIndex[index];
      if (!calloutsForIndex || calloutAnswer === CALLOUT_RESPONSES.DISMISSED) return null;

      const calloutsToRender = calloutsForIndex.filter((callout) =>
        shouldRenderCallout(callout, stableDogSet, calloutAnswer)
      );

      if (calloutsToRender.length === 0) return null;

      return (
        <>
          {calloutsToRender.map((callout) => (
            <TrackedPetInfoCallout
              key={callout.id}
              callout={callout}
              onChange={callout.id === CALLOUT_IDS.DOG_INTERACTION ? handleChange : handleClose}
              onClose={handleClose}
            />
          ))}
        </>
      );
    },
    [isMounted, calloutsPerIndex, handleChange, handleClose, stableDogSet, calloutAnswer]
  );

  return {
    renderCalloutAtIndex,
    toastText,
    onToastClose,
  };
};
